import { useSyncExternalStore } from 'react';

/**
 * The app bar's height, and the height the spacer under it reserves. One constant
 * because the two have to agree exactly: the bar is `position: fixed`, so any gap between
 * them is either a strip of dead band or a row the bar covers.
 */
export const APP_BAR_HEIGHT = { xs: 48, md: 52 } as const;

/** Fallback for routes that render no AppBar at all. */
export const DEFAULT_APP_BAR_HEIGHT = 64;

// Two external stores holding live header measurements, both published by the AppBar
// while it is mounted and reset to their defaults when it unmounts:
//
// - `appBarHeight` — the fixed top bar alone, read by the drawers that align to its
//   lower edge.
// - `headerBlockHeight` — the bar plus the band beneath it (filter row and applied
//   chips), which varies from 64px to ~150px. Read by the surfaces that start below the
//   whole header, such as the full-mode Lift AI panel.
//
// Published by the AppBar rather than pulled from a global
// `document.querySelector('header.MuiAppBar-root')`: that lookup ran once and assumed
// the header always existed, but the drawer reading it is mounted app-wide, including
// on routes that render no AppBar — where `ResizeObserver.observe(null)` threw.
const createHeightStore = (defaultHeight: number) => {
  let height = defaultHeight;
  const listeners = new Set<() => void>();

  const publish = (next: number): void => {
    if (next === height) return;
    height = next;
    for (const listener of listeners) listener();
  };

  const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  return {
    publish,
    reset: () => publish(defaultHeight),
    use: (): number =>
      useSyncExternalStore(
        subscribe,
        () => height,
        () => defaultHeight,
      ),
  };
};

const appBar = createHeightStore(DEFAULT_APP_BAR_HEIGHT);
const headerBlock = createHeightStore(DEFAULT_APP_BAR_HEIGHT);

export const publishAppBarHeight = (height: number): void => appBar.publish(height);
export const resetAppBarHeight = (): void => appBar.reset();
export const useAppBarHeight = (): number => appBar.use();

export const publishHeaderBlockHeight = (height: number): void => headerBlock.publish(height);
export const resetHeaderBlockHeight = (): void => headerBlock.reset();
export const useHeaderBlockHeight = (): number => headerBlock.use();
